import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  MessageCircle, 
  Calendar, 
  ShieldCheck, 
  Users, 
  Check, 
  ChevronRight,
  Smartphone,
  FileX,
  AlertTriangle,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const DEMO_URL = "https://calendar.notion.so/meet/nathalya-ogjfi2k9a/3223a3rry";

// Product Screenshots
const SCREENSHOT_1 = "https://customer-assets.emergentagent.com/job_shift-connect-6/artifacts/24bvbrx0_Screenshot%202026-03-17%20at%203.59.18%E2%80%AFPM.png";
const SCREENSHOT_2 = "https://customer-assets.emergentagent.com/job_shift-connect-6/artifacts/q4fftjm5_Screenshot%202026-03-17%20at%203.59.33%E2%80%AFPM.png";

// Hero Image
const HERO_IMAGE = "https://images.unsplash.com/photo-1773227060944-dc7b00b09ae2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjYXJlZ2l2ZXIlMjBzbWlsaW5nJTIwc2VuaW9yJTIwaG9tZXxlbnwwfHx8fDE3NzM3Nzc3MzB8MA&ixlib=rb-4.1.0&q=85";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Section wrapper with scroll animation
const AnimatedSection = ({ children, className = "", id = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Navigation
const Navigation = () => {
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-junie-cream/80 border-b border-junie-muted/50">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-heading text-2xl font-bold text-junie-navy" data-testid="logo">
            JUNIE<sup className="text-xs">™</sup>
          </span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          <a 
            href="#problem" 
            onClick={(e) => scrollToSection(e, 'problem')}
            className="text-junie-text-muted hover:text-junie-terracotta transition-colors font-medium cursor-pointer" 
            data-testid="nav-problem"
          >
            Problem
          </a>
          <a 
            href="#solution" 
            onClick={(e) => scrollToSection(e, 'solution')}
            className="text-junie-text-muted hover:text-junie-terracotta transition-colors font-medium cursor-pointer" 
            data-testid="nav-solution"
          >
            Solution
          </a>
          <a 
            href="#pricing" 
            onClick={(e) => scrollToSection(e, 'pricing')}
            className="text-junie-text-muted hover:text-junie-terracotta transition-colors font-medium cursor-pointer" 
            data-testid="nav-pricing"
          >
            Pricing
          </a>
        </nav>
        <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
          <Button 
            className="bg-junie-terracotta hover:bg-junie-terracotta-dark text-white rounded-full px-6 py-2 font-semibold transition-all hover:scale-105"
            data-testid="nav-cta-button"
          >
            Book a Demo
          </Button>
        </a>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="min-h-screen pt-24 pb-16 px-6 flex items-center bg-junie-cream" data-testid="hero-section">
    <div className="max-w-7xl mx-auto w-full">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: Text Content */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-left"
        >
          <span className="inline-block bg-junie-muted text-junie-navy px-4 py-2 rounded-full text-sm font-semibold mb-6">
            For Teams That Need Clarity
          </span>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-junie-navy leading-tight tracking-tight mb-6" data-testid="hero-headline">
            Stop Using WhatsApp & Notebooks for Staff Communication
          </h1>
          
          <p className="text-lg lg:text-xl text-junie-text-muted leading-relaxed mb-8 max-w-xl">
            One simple tool to manage shift updates, tasks, and communication—built specifically for your employees. 
            <span className="text-junie-terracotta font-semibold"> Have your team fully onboarded as soon as next week.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="btn-primary bg-junie-terracotta hover:bg-junie-terracotta-dark text-white rounded-full px-8 py-6 text-lg font-bold shadow-lg"
                data-testid="hero-cta-button"
              >
                Book a Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="#product" className="inline-flex items-center justify-center text-junie-navy font-semibold hover:text-junie-terracotta transition-colors group">
              See How It Works
              <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </motion.div>
        
        {/* Right: Image + Floating Card */}
        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          className="relative"
        >
          <div className="relative">
            <div className="img-container rounded-[2rem] shadow-2xl shadow-junie-navy/10 overflow-hidden transform -rotate-2">
              <img 
                src={HERO_IMAGE} 
                alt="Caregiver smiling with senior resident"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            
            {/* Floating UI Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="floating-card absolute -bottom-6 -left-6 lg:-left-12 bg-white p-4 rounded-2xl shadow-xl shadow-junie-navy/10 border border-junie-muted/50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-junie-sage/20 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-junie-sage" />
                </div>
                <div>
                  <p className="font-semibold text-junie-navy text-sm">Shift Update Sent</p>
                  <p className="text-xs text-junie-text-muted">3 staff acknowledged</p>
                </div>
              </div>
            </motion.div>
            
            {/* Second floating element */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="absolute -top-4 -right-4 lg:-right-8 bg-junie-navy text-white p-3 rounded-xl shadow-lg"
              style={{ animationDelay: '2s' }}
            >
              <p className="text-xs font-medium">Live in 1 Week</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = () => {
  const problems = [
    {
      icon: <MessageCircle className="w-6 h-6 text-junie-terracotta" />,
      title: "Staff forget to pass updates",
      description: "Critical information gets lost in endless WhatsApp threads and group chats."
    },
    {
      icon: <FileX className="w-6 h-6 text-junie-terracotta" />,
      title: "Notes get lost between shifts",
      description: "Paper notebooks go missing. Important details never reach the next team."
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-junie-terracotta" />,
      title: "Too many tools",
      description: "Texts, paper, calls, apps—your team is overwhelmed and things slip through."
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-junie-terracotta" />,
      title: "Compliance risks",
      description: "No documentation trail means audit nightmares and liability concerns."
    }
  ];

  return (
    <AnimatedSection className="py-20 lg:py-32 px-6 bg-junie-muted/30" id="problem">
      <div className="max-w-7xl mx-auto" data-testid="problem-section">
        <div className="text-center mb-16">
          <span className="inline-block bg-junie-terracotta/10 text-junie-terracotta px-4 py-2 rounded-full text-sm font-semibold mb-4">
            The Problem
          </span>
          <h2 className="font-heading text-3xl md:text-5xl text-junie-navy tracking-tight mb-4">
            Communication is breaking your operations
          </h2>
          <p className="text-lg text-junie-text-muted max-w-2xl mx-auto">
            Sound familiar? You're not alone. Most staff members are struggling with the same chaos.
          </p>
        </div>
        
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-junie-muted/50"
              data-testid={`problem-card-${index}`}
            >
              <div className="problem-icon">
                {problem.icon}
              </div>
              <h3 className="font-heading text-xl text-junie-navy mb-2">{problem.title}</h3>
              <p className="text-junie-text-muted text-sm leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Solution Section
const SolutionSection = () => {
  const solutions = [
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Shift-to-shift communication",
      description: "Every update is logged and visible. No more lost messages or forgotten details."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Task tracking",
      description: "Assign, track, and complete tasks with clear accountability for every team member."
    },
    {
      icon: <Check className="w-6 h-6" />,
      title: "Read & sign accountability",
      description: "Know exactly who has seen each update. Built-in compliance documentation."
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Mobile-friendly",
      description: "Designed for busy employees and administrators on the go. Your staff will actually use it because it's simple."
    }
  ];

  return (
    <AnimatedSection className="py-20 lg:py-32 px-6 bg-white" id="solution">
      <div className="max-w-7xl mx-auto" data-testid="solution-section">
        <div className="text-center mb-16">
          <span className="inline-block bg-junie-sage/10 text-junie-sage px-4 py-2 rounded-full text-sm font-semibold mb-4">
            The Solution
          </span>
          <h2 className="font-heading text-3xl md:text-5xl text-junie-navy tracking-tight mb-4">
            Everything your team needs—one place
          </h2>
          <p className="text-lg text-junie-text-muted max-w-2xl mx-auto">
            Simple communication tool for shift-based teams. Fix your operations in 1 week.
          </p>
        </div>
        
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 gap-8"
        >
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="flex gap-5 p-6 rounded-2xl hover:bg-junie-muted/20 transition-colors"
              data-testid={`solution-card-${index}`}
            >
              <div className="flex-shrink-0 w-14 h-14 bg-junie-sage/10 rounded-2xl flex items-center justify-center text-junie-sage">
                {solution.icon}
              </div>
              <div>
                <h3 className="font-heading text-xl text-junie-navy mb-2">{solution.title}</h3>
                <p className="text-junie-text-muted leading-relaxed">{solution.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Product Showcase Section
const ProductShowcase = () => (
  <AnimatedSection className="py-20 lg:py-32 px-6 bg-junie-cream" id="product">
    <div className="max-w-7xl mx-auto" data-testid="product-section">
      <div className="text-center mb-16">
        <span className="inline-block bg-junie-gold/20 text-junie-navy px-4 py-2 rounded-full text-sm font-semibold mb-4">
          See It In Action
        </span>
        <h2 className="font-heading text-3xl md:text-5xl text-junie-navy tracking-tight mb-4">
          Simple. Clean. Effective.
        </h2>
        <p className="text-lg text-junie-text-muted max-w-2xl mx-auto">
          A centralized communications log that your entire team can access from any device.
        </p>
      </div>
      
      <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12">
        {/* Tablet/Desktop View */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="device-frame tablet w-full max-w-4xl"
          data-testid="tablet-mockup"
        >
          <div className="device-screen">
            <img 
              src={SCREENSHOT_2} 
              alt="JUNIE communications log interface on tablet"
              className="w-full"
            />
          </div>
        </motion.div>
        
        {/* Phone View */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="device-frame phone hidden lg:block"
          data-testid="phone-mockup"
        >
          <div className="device-screen">
            <img 
              src={SCREENSHOT_1} 
              alt="JUNIE interface on mobile phone"
              className="w-full"
            />
          </div>
        </motion.div>
      </div>
    </div>
  </AnimatedSection>
);

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "JUNIE keeps my team connected and accountable which allows me to focus on supporting them.",
      author: "Executive Director",
      location: "Springpoint Senior Living Manalapan"
    },
    {
      quote: "We're very satisfied with JUNIE. It has been extremely helpful for us in documenting different events in the community and even getting through an AHCA survey.",
      author: "Michael Cherrill",
      location: "Executive Director of two Assisted Living Communities in Broward County"
    }
  ];

  return (
    <AnimatedSection className="py-20 lg:py-32 px-6 bg-junie-navy text-white">
      <div className="max-w-7xl mx-auto" data-testid="testimonials-section">
        <div className="text-center mb-16">
          <span className="inline-block bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            What Our Clients Say
          </span>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight mb-4">
            Trusted by senior living communities
          </h2>
        </div>
        
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="testimonial-card bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10"
              data-testid={`testimonial-card-${index}`}
            >
              <p className="text-xl lg:text-2xl font-heading leading-relaxed mb-6">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-junie-terracotta rounded-full flex items-center justify-center text-white font-bold">
                  {testimonial.author.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-white/60 text-sm">{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Pricing Section
const PricingSection = () => (
  <AnimatedSection className="py-20 lg:py-32 px-6 bg-junie-cream" id="pricing">
    <div className="max-w-4xl mx-auto" data-testid="pricing-section">
      <div className="text-center mb-12">
        <span className="inline-block bg-junie-terracotta/10 text-junie-terracotta px-4 py-2 rounded-full text-sm font-semibold mb-4">
          Simple Pricing
        </span>
        <h2 className="font-heading text-3xl md:text-5xl text-junie-navy tracking-tight mb-4">
          One plan. Everything included.
        </h2>
        <p className="text-lg text-junie-text-muted">
          No hidden fees. No complicated tiers.
        </p>
      </div>
      
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-junie-navy text-white p-10 lg:p-12 rounded-3xl shadow-2xl relative overflow-hidden"
        data-testid="pricing-card"
      >
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-junie-terracotta/10 rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
            <div>
              <h3 className="font-heading text-2xl mb-2">Starter Plan</h3>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-5xl lg:text-6xl font-bold">$299</span>
                <span className="text-white/60">/month</span>
              </div>
            </div>
            <p className="text-junie-gold font-semibold mt-4 md:mt-0">Setup in 1 week</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {[
              "Up to 15 staff members",
              "Unlimited communications",
              "Task management",
              "Read & sign tracking",
              "Mobile app access",
              "Email support"
            ].map((feature, index) => (
              <div key={index} className="check-item">
                <div className="check-icon">
                  <Check className="w-4 h-4" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
          
          <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="block">
            <Button 
              className="w-full bg-junie-terracotta hover:bg-junie-terracotta-dark text-white rounded-full py-6 text-lg font-bold shadow-lg transition-all hover:scale-[1.02]"
              data-testid="pricing-cta-button"
            >
              Book a Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
  </AnimatedSection>
);

// Urgency Section
const UrgencySection = () => (
  <AnimatedSection className="py-12 px-6">
    <div className="max-w-6xl mx-auto" data-testid="urgency-section">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="bg-junie-terracotta text-white rounded-3xl p-10 lg:p-16 text-center"
      >
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight mb-4">
          Get your team live this week
        </h2>
        <p className="text-xl text-white/90 mb-8 max-w-xl mx-auto">
          No contracts. No complicated setup. Just better communication for your employees.
        </p>
        <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
          <Button 
            className="bg-white text-junie-terracotta hover:bg-junie-muted rounded-full px-10 py-6 text-lg font-bold shadow-lg transition-all hover:scale-105"
            data-testid="urgency-cta-button"
          >
            Book a Demo Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </a>
      </motion.div>
    </div>
  </AnimatedSection>
);

// Footer
const Footer = () => (
  <footer className="py-16 px-6 bg-junie-navy text-white" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8">
        <div>
          <span className="font-heading text-2xl font-bold">
            JUNIE<sup className="text-xs">™</sup>
          </span>
          <p className="text-white/60 mt-2">Simple communication for your employees.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              className="bg-junie-terracotta hover:bg-junie-terracotta-dark text-white rounded-full px-8 py-3 font-semibold"
              data-testid="footer-cta-button"
            >
              Book a Demo
            </Button>
          </a>
        </div>
      </div>
      
      <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/40 text-sm">
        <p>© {new Date().getFullYear()} JUNIE. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Main Landing Page
const LandingPage = () => {
  return (
    <main className="overflow-x-hidden" data-testid="landing-page">
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <ProductShowcase />
      <TestimonialsSection />
      <PricingSection />
      <UrgencySection />
      <Footer />
    </main>
  );
};

export default LandingPage;
